<template>
  <div>
    <section class="hero">
      <div class="hero-body">
        <p class="title">{{ i18n.financingPayments[iso] }}</p>
      </div>
    </section>

    <section style="margin-top: 30px">
      <b-tabs v-model="tabFilters" position="is-centered">
        <b-tab-item>
          <template #header>
            <b-icon icon="filter" pack="fas" type="is-info"></b-icon>
            <b>{{ i18n.advancedSearch[iso] }}</b>
          </template>

          <div class="columns is-multiline">
            <!-- Guest ID -->
            <div class="column is-4">
              <b-field label-position="on-border" label="Guest ID">
                <b-input
                  v-model="filters.IdInvitado"
                  type="text"
                  icon-right="times"
                  icon-right-clickable
                  @icon-right-click="filters.IdInvitado = null"
                  @keyup.enter.native="search"
                  expanded
                ></b-input>
              </b-field>
            </div>

            <!-- Nombre del cliente -->
            <div class="column is-4">
              <b-field label-position="on-border" :label="i18n.clientName[iso]">
                <b-input
                  v-model="filters.memberName"
                  type="text"
                  icon-right="times"
                  icon-right-clickable
                  @icon-right-click="filters.memberName = null"
                  @keyup.enter.native="search"
                  expanded
                ></b-input>
              </b-field>
            </div>

            <!-- Email -->
            <div class="column is-4">
              <b-field label-position="on-border" :label="i18n.clientEmail[iso]">
                <b-input
                  v-model="filters.memberEmail"
                  type="email"
                  icon-right="times"
                  icon-right-clickable
                  @icon-right-click="filters.memberEmail = null"
                  @keyup.enter.native="search"
                  expanded
                ></b-input>
              </b-field>
            </div>

            <!-- Referencia (paymentIntentId) -->
            <div class="column is-4">
              <b-field label-position="on-border" :label="i18n.reference[iso]">
                <b-input
                  v-model="filters.paymentIntentId"
                  type="text"
                  icon-right="times"
                  icon-right-clickable
                  @icon-right-click="filters.paymentIntentId = null"
                  @keyup.enter.native="search"
                  expanded
                ></b-input>
              </b-field>
            </div>

            <!-- Fecha de pago (rango) -->
            <div class="column is-4">
              <b-field label-position="on-border" :label="i18n.paymentDate[iso]">
                <b-datepicker
                  v-model="filters.FechaPago"
                  placeholder="DD/MM/YYYY"
                  :min-date="minDate"
                  :max-date="maxDate"
                  :locale="iso"
                  range
                  icon="calendar"
                  icon-pack="fas"
                  :icon-right="filters.FechaPago && filters.FechaPago.length > 0 ? 'times' : ''"
                  icon-right-clickable
                  @icon-right-click="filters.FechaPago = []"
                ></b-datepicker>
              </b-field>
            </div>

            <!-- Status -->
            <div class="column is-4">
              <b-field label-position="on-border" label="Status">
                <b-select v-model="filters.status" expanded>
                  <option value="">Todos</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="succeeded">Succeeded</option>
                  <option value="failed">Failed</option>
                  <option value="expired">Expired</option>
                  <option value="canceled">Canceled</option>
                </b-select>
              </b-field>
            </div>

            <!-- Botones -->
            <div class="column is-4">
              <b-button
                icon-left="search"
                icon-pack="fas"
                type="is-success"
                @click="search"
                expanded
              >{{ i18n.search[iso] }}</b-button>
            </div>
            <div class="column is-4">
              <b-button
                icon-left="redo"
                icon-pack="fas"
                type="is-success is-light"
                @click="clearFilters"
                expanded
              >{{ i18n.resetSearch[iso] }}</b-button>
            </div>
          </div>
        </b-tab-item>
      </b-tabs>

      <hr />

      <b-table
        :data="payments"
        :paginated="isPaginated"
        backend-pagination
        :total="total"
        :per-page="perPage"
        @page-change="onPageChange"
        :current-page.sync="currentPage"
        :pagination-simple="isPaginationSimple"
        :pagination-position="paginationPosition"
        :default-sort-direction="defaultSortDirection"
        :pagination-rounded="isPaginationRounded"
        :sort-icon="sortIcon"
        :sort-icon-size="sortIconSize"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page"
        :striped="true"
        :narrowed="true"
        :hoverable="true"
        :loading="loadingPayments"
        :mobile-cards="hasMobileCards"
      >
        <!-- Guest ID -->
        <b-table-column field="IdInvitado" label="Guest ID" centered v-slot="props">
          <b-tag type="is-dark">
            <b>{{ props.row.IdInvitado || 'n/a' }}</b>
          </b-tag>
        </b-table-column>

        <!-- Nombre del cliente -->
        <b-table-column field="memberName" :label="i18n.clientName[iso]" v-slot="props">
          {{ props.row.memberName || 'n/a' }}
        </b-table-column>

        <!-- Monto -->
        <b-table-column field="totalAmount" :label="i18n.amount[iso]" centered numeric v-slot="props">
          <b-tag type="is-primary is-light">
            <b>{{ formatCurrency(props.row.totalAmount) }}</b>
          </b-tag>
        </b-table-column>

        <!-- Fecha de pago -->
        <b-table-column field="paymentDate" :label="i18n.paymentDate[iso]" centered v-slot="props">
          <b-tag type="is-info is-light">
            <b>{{ formatDate(props.row.paymentDate) }}</b>
          </b-tag>
        </b-table-column>

        <!-- Status -->
        <b-table-column field="status" label="Status" centered v-slot="props">
          <b-tag :type="getStatusTagType(props.row.status)">
            {{ props.row.status || 'n/a' }}
          </b-tag>
        </b-table-column>

        <!-- Concepto -->
        <b-table-column field="concepts" :label="i18n.concept[iso]" centered v-slot="props">
          <span>{{ getConcepto(props.row) }}</span>
        </b-table-column>

        <!-- Referencia -->
        <b-table-column field="paymentIntentId" :label="i18n.reference[iso]" v-slot="props">
          <span style="font-family: monospace; font-size: 0.8em; color: #666;">
            {{ props.row.paymentIntentId || 'n/a' }}
          </span>
        </b-table-column>

        <!-- Footer con paginación -->
        <template #footer>
          <div class="columns">
            <div class="column is-8">
              <div v-if="pagination" style="padding-top: 10px;">
                <span style="font-weight: 500; color: #666;">
                  Total: <strong>{{ pagination.total }}</strong> registros
                </span>
              </div>
            </div>
            <div class="column is-3"></div>
            <div class="column is-1">
              <b-field :label="i18n.quantity[iso]">
                <b-select v-model="perPage" @input="savePerPage" expanded>
                  <option :value="5">5</option>
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                </b-select>
              </b-field>
            </div>
          </div>
        </template>
      </b-table>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import i18n from '../../utils/i18n'

const API_URL = process.env.VUE_APP_API_URL
const today = new Date()

export default {
  name: 'FinancingPayments',
  data() {
    return {
      i18n,
      iso: 'es',

      isPaginated: true,
      isPaginationSimple: true,
      isPaginationRounded: false,
      paginationPosition: 'bottom',
      defaultSortDirection: 'asc',
      sortIcon: 'arrow-up',
      sortIconSize: 'is-small',
      currentPage: 1,
      perPage: 25,
      hasMobileCards: true,
      total: 0,

      payments: [],
      pagination: null,
      loadingPayments: false,

      club: null,
      DEFAULT_PARAMS: {},

      tabFilters: 0,
      filters: {
        IdInvitado: null,
        memberName: null,
        memberEmail: null,
        paymentIntentId: null,
        FechaPago: [],
        status: ''
      },

      minDate: new Date(today.getFullYear() - 10, today.getMonth(), today.getDate()),
      maxDate: new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
    }
  },

  async created() {
    this.iso = localStorage.getItem('iso') || 'es'
    this.$bus.on('change-iso', () => {
      this.iso = localStorage.getItem('iso')
    })

    this.$bus.on('change-club', () => {
      this.getClub()
      this.currentPage = 1
      this.getPayments()
    })
    this.getClub()

    const perPageLS = localStorage.getItem('perPageFinancingNew')
    if (perPageLS) this.perPage = parseInt(perPageLS)

    // Si viene con query param de referencia
    if (this.$route.query.paymentIntentId) {
      this.filters.paymentIntentId = this.$route.query.paymentIntentId
    }
    if (this.$route.query.IdInvitado) {
      this.filters.IdInvitado = this.$route.query.IdInvitado
    }

    await this.getPayments()
  },

  methods: {
    async getPayments() {
      this.loadingPayments = true
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage
        }

        if (this.filters.IdInvitado)      params.IdInvitado      = this.filters.IdInvitado
        if (this.filters.memberName)      params.memberName      = this.filters.memberName
        if (this.filters.memberEmail)     params.memberEmail     = this.filters.memberEmail
        if (this.filters.paymentIntentId) params.paymentIntentId = this.filters.paymentIntentId
        if (this.filters.status)          params.status          = this.filters.status

        // Filtro por club
        if (this.club && this.club.EsClubBase) {
          const children = this.club.CatalogoHijos ? this.club.CatalogoHijos.map(c => c.Id) : []
          params.clubids = [this.club.Id, ...children].join(',')
        } else if (this.DEFAULT_PARAMS.ClubId) {
          params.clubId = this.DEFAULT_PARAMS.ClubId
        }

        if (this.filters.FechaPago && this.filters.FechaPago.length === 2) {
          params.fechaDesde = this.formatDateForAPI(this.filters.FechaPago[0])
          params.fechaHasta = this.formatDateForAPI(this.filters.FechaPago[1])
        }

        const { data } = await axios.get(`${API_URL}/financing/financing-payments`, { params })

        this.payments   = data.data
        this.total      = data.pagination.total
        this.pagination = data.pagination

      } catch (error) {
        console.error('Error obteniendo pagos:', error)
        this.$buefy.toast.open({
          message: 'Error obteniendo los pagos financiados',
          type: 'is-danger'
        })
      } finally {
        this.loadingPayments = false
      }
    },

    search() {
      this.currentPage = 1
      this.getPayments()
    },

    clearFilters() {
      this.filters = {
        IdInvitado: null,
        memberName: null,
        memberEmail: null,
        paymentIntentId: null,
        FechaPago: [],
        status: ''
      }
      this.currentPage = 1
      this.getPayments()
    },

    onPageChange(page) {
      this.currentPage = page
      this.getPayments()
    },

    getClub() {
      let club = localStorage.getItem('club')
      club = club ? JSON.parse(club) : {}
      this.club = club
      this.DEFAULT_PARAMS = {
        ClubId: club?.Id || null,
        _id: club?._id || null
      }
    },

    savePerPage() {
      localStorage.setItem('perPageFinancingNew', this.perPage)
      this.currentPage = 1
      this.getPayments()
    },

    formatCurrency(amount) {
      if (amount === undefined || amount === null) return '-'
      return new Intl.NumberFormat('es-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount)
    },

    formatDate(date) {
      if (!date) return 'n/a'
      moment.locale(this.iso)
      return moment(date).format('ll').toUpperCase()
    },

    formatDateForAPI(date) {
      if (!date) return null
      const d = new Date(date)
      d.setHours(12, 0, 0, 0)
      return d.getTime()
    },

    getStatusTagType(status) {
      switch (status) {
        case 'paid':
        case 'succeeded':  return 'is-success'
        case 'pending':    return 'is-warning'
        case 'failed':     return 'is-danger'
        case 'expired':    return 'is-light'
        case 'canceled':   return 'is-dark'
        default:           return 'is-info is-light'
      }
    },

    getConcepto(row) {
      if (!row.concepts || row.concepts.length === 0) return '-'
      return row.concepts.map(c => c.productName).filter(Boolean).join(', ') || '-'
    }
  }
}
</script>

<style scoped>
/* ESTILACHOS */
</style>